import { useRef, useCallback, useState, useEffect } from 'react';

import { AUTH_TOKEN_KEY } from '../../../constants/local-storage-keys';
import { getCachedBlob, setCachedBlob, removeCachedBlob } from '../../../cache/blobCache';

export default function useAuthFetchBlob({ url, fetchMethod = 'GET' }) {
    const abortRef = useRef();
    const [blobURL, setBlobURL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    const handleAction = useCallback(async () => {
        if (!url) {
            setBlobURL(null);
            return;
        }

        const cached = getCachedBlob(url);

        if (typeof cached === 'string') {
            setBlobURL(cached);
            return;
        }

        // a request is already running
        if (cached instanceof Promise) {
            const resolved = await cached;
            setBlobURL(resolved);
            return;
        }

        setBlobURL(null);
        setLoading(true);
        setError(false);

        abortRef.current = new AbortController();

        const token = localStorage.getItem(AUTH_TOKEN_KEY);

        const promise = fetch(url, {
            method: fetchMethod,
            headers: {
                Authorization: `Bearer ${token}`
            },
            signal: abortRef.current.signal
        })
            .then(async result => {
                if (!result.ok) {
                    throw new Error('Image fetch failed');
                }

                const blob = await result.blob();
                const objectUrl = URL.createObjectURL(blob);

                setCachedBlob(url, objectUrl);

                return objectUrl;
            })
            .catch(err => {
                removeCachedBlob(url);
                throw err;
            });

        setCachedBlob(url, promise);

        try {
            const objectUrl = await promise;
            setBlobURL(objectUrl);
            setLoading(false);
        } catch (err) {
            if (err.name !== 'AbortError') {
                setLoading(false);
                setError(true);
            }
        }
    }, [fetchMethod, url]);

    useEffect(() => {
        handleAction();

        return () => abortRef.current?.abort();
    }, [handleAction]);

    return { loading, error, blobURL };
}
